import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import QuantDashboardRenderer, { isQuantDashboardFormat } from "./QuantDashboardRenderer";
import IPOQuantDashboardRenderer, { isIPOQuantFormat } from "./IPOQuantDashboardRenderer";
import GENAIRenderer from "../GhcAi/AIPages/GENAIRenderer";
import { Block } from "../GhcAi/Utils/ComponentsUtils";

interface QuantTickerDetails {
  ticker: string;
  run_date: string;
  pricing_date: string;
  region: string;
  deal_type: string;
  unique_deal_id: string;
  issuer_name: string;
  sector: string;
  quant_analysis?: any;
  quant_signal?: string;
  created_at: string;
  updated_at: string;
}

const QuantIPODashboardPage: React.FC = () => {
  const params = useParams<{ ticker: string }>();
  const ticker = params.ticker ? decodeURIComponent(params.ticker) : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const rawDealType = searchParams.get("deal_type") || searchParams.get("dealType") || "IPO";
  const dealType = rawDealType.toUpperCase() === "FO" ? "FO" : "IPO";
  const returnPage = searchParams.get("page") || "0";
  const returnRowsPerPage = searchParams.get("rowsPerPage") || "10";

  const apiUrl = process.env.REACT_APP_API_URL;
  const [tickerData, setTickerData] = useState<QuantTickerDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (ticker) fetchDetails();
  }, [ticker, dealType]);

  const fetchDetails = async () => {
    if (!apiUrl || !ticker) {
      setError("Missing required parameters.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("access_token");

      const res = await fetch(`${apiUrl}/api/quant_agent/all_tickers_latest/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ ticker, deal_type: dealType }),
      });

      const data: any = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || data?.detail || `Request failed with status ${res.status}`);
      }

      if (data.data) {
        const info = Array.isArray(data.data) ? data.data[0] : data.data;
        if (info?.quant_analysis && typeof info.quant_analysis === "string") {
          try {
            info.quant_analysis = JSON.parse(info.quant_analysis);
          } catch {
            // keep as string if JSON parse fails
          }
        }
        setTickerData(info);
      } else {
        setError("No data found for this ticker.");
      }
    } catch (err: any) {
      setError(err?.message || "Unable to load quant analysis.");
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (!tickerData?.quant_analysis) return null;

    if (dealType === "IPO" && typeof tickerData.quant_analysis === "object" && !Array.isArray(tickerData.quant_analysis)) {
      return <IPOQuantDashboardRenderer data={tickerData.quant_analysis} />;
    }

    if (isQuantDashboardFormat(tickerData.quant_analysis)) {
      return <QuantDashboardRenderer data={tickerData.quant_analysis} />;
    }

    return <GENAIRenderer blocks={tickerData.quant_analysis as Block[]} renderAll />;
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 3 }}>
      {/* Header */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: 3,
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          p: { xs: 2.5, md: 3 },
          mb: 2.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/quant_agent?deal_type=${dealType}&page=${returnPage}&rowsPerPage=${returnRowsPerPage}`)}
          sx={{
            textTransform: "none",
            color: "#4f46e5",
            fontSize: "0.9rem",
            fontWeight: 600,
            mb: 2,
            "&:hover": { background: "#eef2ff" },
          }}
        >
          Back to Quant Analysis
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <InsightsRoundedIcon sx={{ fontSize: 22, color: "#fff" }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontSize: "1.35rem", fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em" }}
              >
                IPO Quant Dashboard
                <Typography
                  component="span"
                  sx={{ fontSize: "1.35rem", fontWeight: 700, color: "#4f46e5", ml: 0.8 }}
                >
                  {ticker}
                </Typography>
              </Typography>
              {tickerData && (
                <Typography sx={{ fontSize: "0.8rem", color: "#64748b", mt: 0.3 }}>
                  {tickerData.issuer_name} • {tickerData.sector}
                </Typography>
              )}
            </Box>
          </Box>
          {tickerData?.run_date && (
            <Typography sx={{ fontSize: "0.72rem", color: "#4f46e5", background: "#eef2ff", px: 1.5, py: 0.5, borderRadius: 2, fontWeight: 600, border: "1px solid #c7d2fe", flexShrink: 0 }}>
              Run Date: {new Date(tickerData.run_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 12,
            gap: 2,
          }}
        >
          <CircularProgress size={48} thickness={4} sx={{ color: "#4f46e5" }} />
          <Typography sx={{ fontSize: "0.9rem", color: "#94a3b8", fontWeight: 500 }}>
            Loading IPO dashboard for {ticker}...
          </Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2.5, border: "1px solid #fecaca", "& .MuiAlert-message": { fontSize: "0.85rem" } }}
        >
          {error}
        </Alert>
      )}

      {/* Dashboard Content */}
      {!loading && tickerData && renderContent()}

      {/* No Data */}
      {!loading && !error && (!tickerData || !tickerData.quant_analysis) && (
        <Box
          sx={{ py: 12, textAlign: "center", background: "#ffffff", borderRadius: 3, border: "1px solid #e2e8f0" }}
        >
          <InsightsRoundedIcon sx={{ fontSize: 48, color: "#e2e8f0", mb: 2 }} />
          <Typography sx={{ color: "#94a3b8", fontSize: "0.9rem" }}>
            No quant analysis data available for this ticker.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default QuantIPODashboardPage;
